import { addDays, addHours } from 'date-fns';

import {
  addBreachNotificationInvocationBreadcrumb,
  captureBreachNotificationInvokeFailure,
  captureBulkDispositionChunkFailure,
} from '@/lib/breachNotifications.sentry';
import { supabase } from '@/integrations/supabase/client';
import { buildFunctionInvokeError } from '@/lib/functionInvokeErrors';
import { invokeProtectedFunction } from '@/lib/protectedFunctionAuth';
import { LEGACY_STORAGE_BUCKETS } from './storageBuckets';

export const BREACH_NOTIFICATION_EVENT_TYPES = [
  'breach.initial_notice',
  'breach.status_update',
  'breach.follow_up_due',
  'breach.acknowledgement_overdue',
  'breach.final_report_available',
] as const;

export type BreachNotificationEventType = typeof BREACH_NOTIFICATION_EVENT_TYPES[number];
export type BreachIncidentStatus = 'suspected' | 'confirmed';
export type BreachCaseSeverity = 'critical' | 'high' | 'medium' | 'low' | 'unknown';
export type BreachReceiverRole = 'security' | 'legal_dpo' | 'executive' | 'backup' | 'supplier_incident_approver';

export const BREACH_REGULATORY_FRAMEWORK_OPTIONS = [
  { value: 'uk_csr_nis', label: 'UK CSR / NIS-style' },
  { value: 'uk_gdpr', label: 'UK GDPR' },
  { value: 'eu_nis2', label: 'EU NIS2-style' },
  { value: 'pci_dss', label: 'PCI DSS' },
  { value: 'contractual', label: 'Contractual clause' },
] as const;

export type BreachRegulatoryFramework = typeof BREACH_REGULATORY_FRAMEWORK_OPTIONS[number]['value'];

export interface BreachCaseDraft {
  title: string;
  severity: BreachCaseSeverity;
  incidentStatus: BreachIncidentStatus;
  awarenessAt: string;
  affectedService: string;
  description: string;
}

export interface BreachNotificationCase {
  id: string;
  title: string;
  description: string;
  status: string;
  severity: string;
  incident_status: string;
  affected_service: string;
  containment_status: string | null;
  supplier_name: string;
  supplier_organization_id: string;
  supplier_vendor_profile_id: string;
  marketplace_listing_id: string;
  supplier_discovered_at: string | null;
  supplier_reported_at?: string | null;
  platform_awareness_at?: string | null;
  initial_notice_due_at: string;
  full_notice_due_at: string;
  final_report_due_at: string;
  last_notice_sent_at: string | null;
  opened_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  created_at: string;
}

export const BREACH_EVIDENCE_MAX_FILES = 5;
export const BREACH_EVIDENCE_MAX_BYTES = 10 * 1024 * 1024;
export const BREACH_EVIDENCE_ACCEPT = '.pdf,.docx,.xlsx,.csv,.txt,.jpg,.jpeg,.png';

const breachEvidenceContentTypesByExtension: Record<string, string> = {
  csv: 'text/csv',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  pdf: 'application/pdf',
  png: 'image/png',
  txt: 'text/plain',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

export interface BreachCaseEvidence {
  id: string;
  breach_case_id: string;
  original_file_name: string;
  content_type: string;
  file_size_bytes: number;
  sha256: string;
  available_at: string;
  created_at: string;
}

export type BreachEvidencePortal = 'customer' | 'vendor';

export type NewsAlertCustomerStatus = 'new' | 'under_investigation' | 'resolved' | 'not_applicable';
export type SupplierAlertCustomerStatus = NewsAlertCustomerStatus;

export interface BreachArticleCustomerDisposition {
  id: string;
  breach_article_id: string;
  customer_organization_id: string;
  status: NewsAlertCustomerStatus;
  opened_at: string;
  notified_at: string;
  status_changed_at: string;
  status_changed_by: string | null;
  first_viewed_at: string | null;
  first_viewed_by: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_notes: string | null;
}

export interface NewsAlertHistoryEvent {
  id: string;
  breach_article_id: string;
  customer_organization_id: string;
  event_type: 'news_alert.notified' | 'news_alert.viewed' | 'news_alert.status_changed' | 'news_alert.action_created' | 'news_alert.action_status_changed' | 'news_alert.action_completed';
  event_at: string;
  actor_user_id: string | null;
  event_source: string;
  source_table: string | null;
  source_id: string | null;
  summary: string;
  metadata: Record<string, unknown>;
  actor: { displayName: string; email: string | null } | null;
}

export interface SupplierAlertHistoryEvent {
  id: string;
  breach_case_id: string;
  customer_organization_id: string;
  event_type: string;
  event_at: string;
  actor_user_id: string | null;
  event_source: string;
  source_table: string | null;
  source_id: string | null;
  summary: string;
  metadata: Record<string, unknown>;
  actor: { displayName: string; email: string | null } | null;
}

export interface NewsAlertLinkedAction {
  id: string;
  supplier_id: string | null;
  title: string;
  status: string;
  severity: string;
  owner_user_id: string | null;
  due_date: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CustomerBreachNotice {
  id: string;
  breach_case_id: string;
  customer_organization_id: string;
  customer_supplier_id: string | null;
  include_reason: string;
  notification_status: string;
  customer_status: SupplierAlertCustomerStatus;
  customer_status_changed_at: string;
  customer_status_changed_by: string | null;
  customer_resolved_at: string | null;
  customer_resolved_by: string | null;
  customer_resolution_notes: string | null;
  initial_notice_sent_at: string | null;
  last_notice_sent_at: string | null;
  follow_up_due_at: string | null;
  next_follow_up_due_at?: string | null;
  last_follow_up_sent_at?: string | null;
  notification_event_count?: number | null;
  recipient_user_ids?: string[] | null;
  receiver_role_snapshot?: string[] | null;
  obligation_snapshot?: BreachObligationEvidenceSnapshot | null;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  breach_notification_cases?: BreachNotificationCase | null;
}

export interface CanonicalSupplierLogo {
  marketplaceListingId: string;
  supplierName: string;
  logoUrl: string | null;
}

export interface BreachCustomerCandidate {
  supplierId: string;
  organizationId: string;
  organizationName: string;
  supplierName: string;
  criticalityTier: string | null;
  serviceDescription: string | null;
}

export interface BreachReceiverMember {
  userId: string;
  role: string;
  email: string | null;
  displayName: string;
  receiverRoles: Array<{
    role: string;
    enabled: boolean;
  }>;
}

/**
 * Which rung of the supplier-breach delivery ladder is in effect for an org
 * (ENT-4719). Decided backend-side by `resolveDeliveryPlan` and returned by the
 * `list_receivers` read model so the Team Alerts panel renders it as a
 * pass-through instead of recomputing the tier client-side.
 */
export type BreachDeliveryTier = 'receivers' | 'admin_fallback' | 'none';

export interface BreachDeliveryPlan {
  tier: BreachDeliveryTier;
  /** The user ids the notify path will actually deliver individual notices to. */
  recipientUserIds: string[];
}

export interface BreachReceiverAssignment {
  userId: string;
  role: BreachReceiverRole | string;
  enabled: boolean;
}

export type BreachObligationSourceSide = 'customer' | 'supplier' | 'platform';
export type BreachObligationConfigurationMode = 'simple' | 'advanced';
export type BreachObligationConfirmationStatus =
  | 'draft'
  | 'customer_asserted'
  | 'supplier_asserted'
  | 'matched'
  | 'conflict'
  | 'confirmed'
  | 'superseded';

export interface BreachNotificationObligation {
  id: string;
  customer_organization_id: string;
  supplier_organization_id: string | null;
  supplier_vendor_profile_id: string | null;
  marketplace_listing_id: string;
  source_side: BreachObligationSourceSide;
  configuration_mode: BreachObligationConfigurationMode;
  confirmation_status: BreachObligationConfirmationStatus;
  notify_all_linked_customers: boolean;
  initial_notice_hours: number;
  full_notice_hours: number;
  final_report_days: number;
  escalation_cadence_hours: number | null;
  reminder_lead_hours: number | null;
  contract_reference: string | null;
  clause_reference: string | null;
  regulatory_frameworks: BreachRegulatoryFramework[];
  escalation_contact_role: BreachReceiverRole | string | null;
  decision_notes: string | null;
  scope_description: string | null;
  effective_at: string;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BreachNotificationObligationDraft {
  customerOrganizationId?: string;
  marketplaceListingId?: string;
  supplierOrganizationId?: string | null;
  supplierVendorProfileId?: string | null;
  configurationMode?: BreachObligationConfigurationMode;
  confirmationStatus?: BreachObligationConfirmationStatus;
  notifyAllLinkedCustomers?: boolean;
  initialNoticeHours?: number;
  fullNoticeHours?: number;
  finalReportDays?: number;
  escalationCadenceHours?: number | null;
  reminderLeadHours?: number | null;
  contractReference?: string | null;
  clauseReference?: string | null;
  regulatoryFrameworks?: BreachRegulatoryFramework[];
  escalationContactRole?: BreachReceiverRole | string | null;
  decisionNotes?: string | null;
  scopeDescription?: string | null;
}

export interface BreachNotificationTimelineConfig {
  initialNoticeHours?: number;
  fullNoticeHours?: number;
  finalReportDays?: number;
}

export interface BreachNotificationTimeline {
  initialNoticeDueAt: string;
  fullNoticeDueAt: string;
  finalReportDueAt: string;
}

export type BreachObligationState = 'acknowledgement_due' | 'follow_up_overdue' | 'acknowledged' | 'closed';

export interface BreachObligationStatus {
  state: BreachObligationState;
  label: string;
  dueAt: string | null;
}

export interface BreachEvidencePackSummary {
  caseId: string;
  noticeCount: number;
  acknowledgedCount: number;
  pendingAcknowledgementCount: number;
  receiverCount: number;
  notificationEventCount: number;
  receiverRoles: string[];
  latestNoticeAt: string | null;
  coverageLabel: string;
  recipientCoverageLabel: string;
}

export interface BreachObligationEvidenceSnapshot {
  status?: 'matched' | 'conflict' | 'single_source' | 'none' | string;
  source_sides?: string[];
  initial_notice_hours?: number;
  full_notice_hours?: number;
  final_report_days?: number;
  escalation_cadence_hours?: number | null;
  reminder_lead_hours?: number | null;
  contract_references?: string[];
  clause_references?: string[];
  regulatory_frameworks?: BreachRegulatoryFramework[];
  escalation_contact_roles?: string[];
  decision_notes?: string[];
  scope_descriptions?: string[];
}

export interface BreachObligationComparison {
  state: 'none' | 'single_source' | 'matched' | 'conflict';
  label: string;
  conflictFields: string[];
}

export interface BreachReceiverCandidate {
  userId: string | null;
  role: BreachReceiverRole | string;
}

export function computeBreachNotificationTimeline(
  awarenessAt: string,
  config: BreachNotificationTimelineConfig = {},
): BreachNotificationTimeline {
  const awarenessDate = new Date(awarenessAt);
  const initialNoticeHours = config.initialNoticeHours ?? 24;
  const fullNoticeHours = config.fullNoticeHours ?? 72;
  const finalReportDays = config.finalReportDays ?? 30;

  return {
    initialNoticeDueAt: addHours(awarenessDate, initialNoticeHours).toISOString(),
    fullNoticeDueAt: addHours(awarenessDate, fullNoticeHours).toISOString(),
    finalReportDueAt: addDays(awarenessDate, finalReportDays).toISOString(),
  };
}

export function getBreachRegulatoryFrameworkLabel(value: string): string {
  return BREACH_REGULATORY_FRAMEWORK_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

export function getBreachNotificationSeverity(severity: string): 'critical' | 'high' | 'medium' | 'low' | 'info' {
  if (severity === 'critical' || severity === 'high' || severity === 'medium' || severity === 'low') {
    return severity;
  }

  return 'info';
}

function isPast(value: string | null | undefined, now: string | Date): boolean {
  if (!value) return false;
  const dueAt = new Date(value).getTime();
  const nowAt = typeof now === 'string' ? new Date(now).getTime() : now.getTime();
  return !Number.isNaN(dueAt) && !Number.isNaN(nowAt) && dueAt < nowAt;
}

export function getBreachObligationStatus(
  notice: CustomerBreachNotice,
  now: string | Date = new Date(),
): BreachObligationStatus {
  if (notice.notification_status === 'closed') {
    return { state: 'closed', label: 'Closed', dueAt: null };
  }

  if (notice.acknowledged_at) {
    return { state: 'acknowledged', label: 'Acknowledged', dueAt: notice.acknowledged_at };
  }

  if (isPast(notice.follow_up_due_at, now)) {
    return {
      state: 'follow_up_overdue',
      label: 'Follow-up overdue',
      dueAt: notice.follow_up_due_at,
    };
  }

  return {
    state: 'acknowledgement_due',
    label: 'Acknowledgement due',
    dueAt: notice.follow_up_due_at ?? notice.last_notice_sent_at,
  };
}

export function buildBreachAlertDeepLink({
  caseId,
  noticeId,
}: {
  caseId?: string | null;
  noticeId?: string | null;
} = {}): string {
  const params = new URLSearchParams();
  if (caseId) params.set('case', caseId);
  if (noticeId) params.set('notice', noticeId);
  const query = params.toString();
  return query ? `/breach-notifications?${query}` : '/breach-notifications';
}

export function buildBreachEvidencePackSummary({
  caseId,
  noticeCount,
  acknowledgedCount,
  receiverCount,
  notificationEventCount,
  receiverRoles = [],
  latestNoticeAt,
}: {
  caseId: string;
  noticeCount: number;
  acknowledgedCount: number;
  receiverCount: number;
  notificationEventCount?: number | null;
  receiverRoles?: string[] | null;
  latestNoticeAt: string | null;
}): BreachEvidencePackSummary {
  const safeNoticeCount = Math.max(0, noticeCount);
  const safeAcknowledgedCount = Math.min(Math.max(0, acknowledgedCount), safeNoticeCount);
  const safeReceiverCount = Math.max(0, receiverCount);
  const safeNotificationEventCount = Math.max(0, notificationEventCount ?? 0);
  const uniqueReceiverRoles = Array.from(new Set((receiverRoles ?? []).filter(Boolean)));

  return {
    caseId,
    noticeCount: safeNoticeCount,
    acknowledgedCount: safeAcknowledgedCount,
    pendingAcknowledgementCount: safeNoticeCount - safeAcknowledgedCount,
    receiverCount: safeReceiverCount,
    notificationEventCount: safeNotificationEventCount,
    receiverRoles: uniqueReceiverRoles,
    latestNoticeAt,
    coverageLabel: `${safeAcknowledgedCount} of ${safeNoticeCount} acknowledged`,
    recipientCoverageLabel: `${safeReceiverCount} breach receiver${safeReceiverCount === 1 ? '' : 's'}, ${safeNotificationEventCount} delivery event${safeNotificationEventCount === 1 ? '' : 's'}`,
  };
}

function sortedStrings(values: Array<string | null | undefined>): string[] {
  return Array.from(new Set(values.filter((value): value is string => Boolean(value)))).sort();
}

function comparableValue(value: unknown): string {
  if (Array.isArray(value)) {
    return sortedStrings(value).join('|');
  }

  return value === null || value === undefined ? '' : String(value);
}

export function compareBreachNotificationObligations(
  customerObligation: BreachNotificationObligation | null | undefined,
  supplierObligation: BreachNotificationObligation | null | undefined,
): BreachObligationComparison {
  if (!customerObligation && !supplierObligation) {
    return { state: 'none', label: 'No terms configured', conflictFields: [] };
  }

  if (!customerObligation || !supplierObligation) {
    return {
      state: 'single_source',
      label: customerObligation ? 'Customer asserted' : 'Supplier asserted',
      conflictFields: [],
    };
  }

  const comparedFields: Array<[keyof BreachNotificationObligation, string]> = [
    ['notify_all_linked_customers', 'notification scope'],
    ['initial_notice_hours', 'initial notice hours'],
    ['full_notice_hours', 'full notice hours'],
    ['final_report_days', 'final report days'],
    ['escalation_cadence_hours', 'escalation cadence'],
    ['reminder_lead_hours', 'reminder lead time'],
    ['contract_reference', 'contract reference'],
    ['clause_reference', 'clause reference'],
    ['regulatory_frameworks', 'regulatory packs'],
  ];

  const conflictFields = comparedFields
    .filter(([field]) => comparableValue(customerObligation[field]) !== comparableValue(supplierObligation[field]))
    .map(([, label]) => label);

  return conflictFields.length > 0
    ? { state: 'conflict', label: 'Terms conflict', conflictFields }
    : { state: 'matched', label: 'Terms matched', conflictFields: [] };
}

export function resolveBreachReceiverRecipients({
  configuredReceivers,
  fallbackAdminUserIds,
}: {
  configuredReceivers: BreachReceiverCandidate[];
  fallbackAdminUserIds: string[];
}): string[] {
  const configured = configuredReceivers
    .map((receiver) => receiver.userId)
    .filter((userId): userId is string => Boolean(userId));

  const source = configured.length > 0 ? configured : fallbackAdminUserIds;
  return Array.from(new Set(source));
}

export function validateBreachCaseDraft(draft: BreachCaseDraft): string[] {
  const errors: string[] = [];

  if (!draft.title.trim()) {
    errors.push('title is required');
  }

  if (!draft.affectedService.trim()) {
    errors.push('affected service is required');
  }

  if (!draft.description.trim()) {
    errors.push('description is required');
  }

  if (Number.isNaN(new Date(draft.awarenessAt).getTime())) {
    errors.push('awareness time must be a valid date');
  }

  return errors;
}

export function buildBreachNotificationMessage({
  supplierName,
  incidentStatus,
  affectedService,
  containmentStatus,
  fullNoticeDueAt,
}: {
  supplierName: string;
  incidentStatus: BreachIncidentStatus | string;
  affectedService: string;
  containmentStatus: string | null;
  fullNoticeDueAt: string;
}): string {
  const containment = containmentStatus?.trim() || 'Not yet confirmed';
  const dueLabel = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  }).format(new Date(fullNoticeDueAt)).replace(',', '');

  return `${supplierName} reported a ${incidentStatus} breach affecting ${affectedService}. Current containment status: ${containment}. A fuller update is due by ${dueLabel}.`;
}

async function invokeBreachNotificationCases<TResponse>(
  body: Record<string, unknown>,
  fallback: string,
  options: { signal?: AbortSignal } = {},
): Promise<TResponse> {
  const action = typeof body.action === 'string' ? body.action : 'unknown';
  const portal = typeof body.portal === 'string' ? body.portal : undefined;
  const observabilityContext = { action, portal };
  const isBulkDisposition = action === 'set_supplier_dispositions_bulk' || action === 'set_news_dispositions_bulk';

  addBreachNotificationInvocationBreadcrumb(observabilityContext);

  try {
    const invoke = options.signal
      ? invokeProtectedFunction<TResponse>(
        'breach-notification-cases',
        body,
        { signal: options.signal },
      )
      : invokeProtectedFunction<TResponse>('breach-notification-cases', body);
    const { data, error, response } = await invoke;

    if (error || !data) {
      const normalizedError = await buildFunctionInvokeError(
        'breach-notification-cases',
        error,
        fallback,
        response,
      );
      throw normalizedError;
    }

    return data;
  } catch (error) {
    if (!isBulkDisposition) {
      await captureBreachNotificationInvokeFailure(error, observabilityContext);
    }
    throw error;
  }
}

export async function listSupplierBreachCases({
  organizationId,
  vendorProfileId,
}: {
  organizationId: string;
  vendorProfileId: string;
}) {
  return invokeBreachNotificationCases<{ cases: BreachNotificationCase[] }>(
    {
      action: 'list_cases',
      portal: 'vendor',
      organizationId,
      vendorProfileId,
    },
    'Breach cases failed to load',
  );
}

export async function listCustomerBreachNotices({
  organizationId,
  signal,
}: {
  organizationId: string;
  signal?: AbortSignal;
}) {
  return invokeBreachNotificationCases<{ cases: CustomerBreachNotice[] }>(
    {
      action: 'list_cases',
      portal: 'customer',
      organizationId,
    },
    'Customer breach notices failed to load',
    { signal },
  );
}

export async function listCanonicalSupplierLogos({
  marketplaceListingIds,
  supplierNames = [],
}: {
  marketplaceListingIds: string[];
  supplierNames?: string[];
}): Promise<{ logos: CanonicalSupplierLogo[] }> {
  const uniqueListingIds = Array.from(new Set(marketplaceListingIds.filter(Boolean)));
  const uniqueSupplierNames = Array.from(new Set(supplierNames.map((name) => name.trim()).filter(Boolean)));
  if (uniqueListingIds.length === 0 && uniqueSupplierNames.length === 0) return { logos: [] };

  const columns = 'id, company_name, ent5657_display_logo_url';
  const [listingResult, nameResult] = await Promise.all([
    uniqueListingIds.length > 0
      ? supabase.from('marketplace_listings').select(columns).in('id', uniqueListingIds)
      : Promise.resolve({ data: [], error: null }),
    uniqueSupplierNames.length > 0
      ? supabase.from('marketplace_listings').select(columns).in('company_name', uniqueSupplierNames)
      : Promise.resolve({ data: [], error: null }),
  ]);

  const error = listingResult.error ?? nameResult.error;
  if (error) {
    throw new Error(error.message || 'Canonical supplier logos failed to load');
  }

  const listings = new Map(
    [...(listingResult.data ?? []), ...(nameResult.data ?? [])]
      .map((listing) => [listing.id, listing] as const),
  );

  return {
    logos: Array.from(listings.values()).map((listing) => ({
      marketplaceListingId: listing.id,
      supplierName: listing.company_name,
      logoUrl: listing.ent5657_display_logo_url,
    })),
  };
}

export async function listBreachCustomerCandidates({
  organizationId,
  vendorProfileId,
}: {
  organizationId: string;
  vendorProfileId: string;
}) {
  return invokeBreachNotificationCases<{ customers: BreachCustomerCandidate[] }>(
    {
      action: 'list_customer_candidates',
      organizationId,
      vendorProfileId,
    },
    'Impacted customers failed to load',
  );
}

export async function createSupplierBreachCase({
  draft,
  organizationId,
  vendorProfileId,
}: {
  draft: BreachCaseDraft & {
    containmentStatus?: string | null;
    dataCategories?: string[];
    discoveredAt?: string | null;
  };
  organizationId: string;
  vendorProfileId: string;
}) {
  return invokeBreachNotificationCases<{ case: BreachNotificationCase }>(
    {
      action: 'create_case',
      organizationId,
      vendorProfileId,
      caseDraft: draft,
    },
    'Breach case could not be created',
  );
}

export async function updateSupplierBreachCase({
  caseId,
  draft,
  organizationId,
  vendorProfileId,
}: {
  caseId: string;
  draft: BreachCaseDraft & { containmentStatus?: string | null };
  organizationId: string;
  vendorProfileId: string;
}) {
  return invokeBreachNotificationCases<{ case: BreachNotificationCase; changedFields: string[] }>(
    {
      action: 'update_supplier_case',
      organizationId,
      portal: 'vendor',
      vendorProfileId,
      caseId,
      caseDraft: draft,
    },
    'Breach details could not be updated',
  );
}

export function getBreachEvidenceContentType(file: Pick<File, 'name' | 'type'>) {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return breachEvidenceContentTypesByExtension[extension] ?? file.type.toLowerCase();
}

export function validateBreachEvidenceFiles(files: File[]) {
  if (files.length > BREACH_EVIDENCE_MAX_FILES) {
    return [`Attach no more than ${BREACH_EVIDENCE_MAX_FILES} evidence files.`];
  }

  return files.flatMap((file) => {
    const errors: string[] = [];
    const contentType = getBreachEvidenceContentType(file);
    if (!Object.values(breachEvidenceContentTypesByExtension).includes(contentType)) {
      errors.push(`${file.name}: use PDF, DOCX, XLSX, CSV, TXT, JPG or PNG.`);
    }
    if (file.size < 1 || file.size > BREACH_EVIDENCE_MAX_BYTES) {
      errors.push(`${file.name}: files must be between 1 byte and 10 MB.`);
    }
    return errors;
  });
}

async function getFileSha256(file: File) {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export async function uploadBreachEvidenceFile({
  caseId,
  file,
  organizationId,
  vendorProfileId,
}: {
  caseId: string;
  file: File;
  organizationId: string;
  vendorProfileId: string;
}) {
  const validationErrors = validateBreachEvidenceFiles([file]);
  if (validationErrors.length > 0) {
    throw new Error(validationErrors.join(' '));
  }

  const contentType = getBreachEvidenceContentType(file);
  const sha256 = await getFileSha256(file);
  const prepared = await invokeBreachNotificationCases<{
    evidence: { id: string; storagePath: string; uploadToken: string };
  }>(
    {
      action: 'prepare_evidence_upload',
      portal: 'vendor',
      organizationId,
      vendorProfileId,
      caseId,
      fileName: file.name,
      contentType,
      fileSizeBytes: file.size,
      sha256,
    },
    'Evidence upload could not be prepared',
  );

  const { error: uploadError } = await supabase.storage
    .from(LEGACY_STORAGE_BUCKETS.BREACH_EVIDENCE)
    .uploadToSignedUrl(
      prepared.evidence.storagePath,
      prepared.evidence.uploadToken,
      file,
      { contentType, upsert: false },
    );
  if (uploadError) {
    throw new Error(uploadError.message || 'Evidence file could not be uploaded');
  }

  return invokeBreachNotificationCases<{ availableAt: string; evidenceId: string }>(
    {
      action: 'complete_evidence_upload',
      portal: 'vendor',
      organizationId,
      vendorProfileId,
      caseId,
      evidenceId: prepared.evidence.id,
    },
    'Evidence upload could not be completed',
  );
}

export async function listBreachCaseEvidence({
  caseId,
  organizationId,
  portal,
  vendorProfileId,
}: {
  caseId: string;
  organizationId: string;
  portal: BreachEvidencePortal;
  vendorProfileId?: string;
}) {
  return invokeBreachNotificationCases<{ evidence: BreachCaseEvidence[] }>(
    {
      action: 'list_case_evidence',
      organizationId,
      portal,
      vendorProfileId,
      caseId,
    },
    'Evidence files could not be loaded',
  );
}

export async function getBreachEvidenceDownloadUrl({
  caseId,
  evidenceId,
  organizationId,
  portal,
  vendorProfileId,
}: {
  caseId: string;
  evidenceId: string;
  organizationId: string;
  portal: BreachEvidencePortal;
  vendorProfileId?: string;
}) {
  return invokeBreachNotificationCases<{ expiresAt: string; signedUrl: string }>(
    {
      action: 'get_evidence_download_url',
      organizationId,
      portal,
      vendorProfileId,
      caseId,
      evidenceId,
    },
    'Evidence file could not be opened',
  );
}

export async function notifyBreachCustomers({
  caseId,
  customerOrganizationIds,
  includeReason,
  messageOverride,
  organizationId,
}: {
  caseId: string;
  customerOrganizationIds: string[];
  includeReason: string;
  messageOverride?: string | null;
  organizationId: string;
}) {
  return invokeBreachNotificationCases<{
    customerCount: number;
    notificationCount: number;
  }>(
    {
      action: 'notify_customers',
      organizationId,
      caseId,
      customerOrganizationIds,
      includeReason,
      messageOverride: messageOverride ?? null,
    },
    'Customer breach notifications could not be sent',
  );
}

export async function acknowledgeBreachNotice({
  caseId,
  organizationId,
}: {
  caseId: string;
  organizationId: string;
}) {
  return invokeBreachNotificationCases<{ acknowledgedAt: string }>(
    {
      action: 'acknowledge_customer',
      organizationId,
      caseId,
    },
    'Breach notice acknowledgement could not be saved',
  );
}

export async function setSupplierAlertStatus({
  caseId,
  organizationId,
  status,
  resolutionNotes,
}: {
  caseId: string;
  organizationId: string;
  status: SupplierAlertCustomerStatus;
  resolutionNotes?: string | null;
}) {
  return invokeBreachNotificationCases<{ notice: CustomerBreachNotice }>(
    {
      action: 'set_supplier_disposition',
      organizationId,
      caseId,
      dispositionStatus: status,
      resolutionNotes: resolutionNotes ?? null,
    },
    'Supplier Alert status could not be saved',
  );
}

// ENT-2733: one entry per requested id from a bulk disposition action. `ok`
// false carries the per-item failure so the UI can report partial success.
export interface BreachBulkDispositionResult {
  id: string;
  ok: boolean;
  errorCode?: string;
  error?: string;
}

// ENT-2733 (Codex P2 round 7): mirror the edge function's MAX_BULK_DISPOSITIONS
// cap. A select-all over a frequently-reported supplier can exceed the cap (the
// feed loads in 100-row pages), which the backend rejects with a 400 for the whole
// batch, so we chunk the request client-side and each chunk is one bounded Edge
// invocation. The cap is deliberately small (per-item writers run several serial
// DB ops) so no single invocation approaches the execution window; larger
// selections simply become more chunks. Keep this in lockstep with the backend
// MAX_BULK_DISPOSITIONS.
export const BREACH_BULK_DISPOSITION_LIMIT = 50;

function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
}

// ENT-2733 (Codex P2 round 3): each chunk is its own backend call, so a later
// chunk's transport/function-level rejection must NOT discard the rows an earlier
// chunk already committed. Fold a failed chunk's ids into results{ ok:false } and
// keep going, so the bulk helpers always return the accumulated outcomes for
// handleBulkStatusChange to reconcile partial success (the mutation stays a
// backend operation per item; the client only fans out and aggregates).
function foldFailedChunk(
  results: BreachBulkDispositionResult[],
  ids: string[],
  error: unknown,
): void {
  const message = error instanceof Error ? error.message : 'Alert status could not be saved';
  for (const id of ids) {
    results.push({ id, ok: false, errorCode: 'bulk_chunk_request_failed', error: message });
  }
}

// ENT-2733 (Codex P2 round 9): the backend stamps this errorCode on every item it
// fails when a chunk aborts on a SYSTEMIC dependency failure (mirror of the
// backend BULK_SYSTEMIC_FAILURE_CODE literal). Keep the two in lockstep.
const BULK_SYSTEMIC_FAILURE_CODE = 'bulk_systemic_failure';

// A chunk is systemic if it threw at the transport/whole-function boundary (already
// folded as bulk_chunk_request_failed) or it returned 200 but the backend aborted
// mid-loop on a shared-dependency failure (bulk_systemic_failure). In both cases
// the same dependency is down, so issuing the remaining chunks just repeats the
// failing path and emits another Sentry event per chunk — stop and mark the rest.
function chunkResultsAreSystemic(results: BreachBulkDispositionResult[]): boolean {
  return results.some((result) => result.errorCode === BULK_SYSTEMIC_FAILURE_CODE);
}

function failUnscheduledChunks(
  results: BreachBulkDispositionResult[],
  remainingChunks: string[][],
): void {
  for (const batch of remainingChunks) {
    for (const id of batch) {
      results.push({
        id,
        ok: false,
        errorCode: 'bulk_skipped_after_systemic',
        error: 'Skipped after a systemic failure earlier in the batch.',
      });
    }
  }
}

// ENT-2733: mark many Supplier Alerts (breach cases) in one or more backend
// requests (chunked at BREACH_BULK_DISPOSITION_LIMIT), aggregating results.
export async function setSupplierAlertStatusBulk({
  caseIds,
  organizationId,
  status,
}: {
  caseIds: string[];
  organizationId: string;
  status: SupplierAlertCustomerStatus;
}) {
  const notices: CustomerBreachNotice[] = [];
  const results: BreachBulkDispositionResult[] = [];
  const chunks = chunk(caseIds, BREACH_BULK_DISPOSITION_LIMIT);
  for (let index = 0; index < chunks.length; index++) {
    const batch = chunks[index];
    let systemic = false;
    try {
      const response = await invokeBreachNotificationCases<{
        notices: CustomerBreachNotice[];
        results: BreachBulkDispositionResult[];
      }>(
        {
          action: 'set_supplier_dispositions_bulk',
          organizationId,
          caseIds: batch,
          dispositionStatus: status,
        },
        'Supplier Alert statuses could not be saved',
      );
      notices.push(...response.notices);
      results.push(...response.results);
      systemic = chunkResultsAreSystemic(response.results);
    } catch (error) {
      await captureBulkDispositionChunkFailure(error, {
        action: 'set_supplier_dispositions_bulk',
        organizationId,
        batchSize: batch.length,
      });
      foldFailedChunk(results, batch, error);
      systemic = true;
    }
    // Round 9: stop fanning out further chunks at a dependency that just failed
    // systemically; mark the unscheduled ids failed without more requests.
    if (systemic) {
      failUnscheduledChunks(results, chunks.slice(index + 1));
      break;
    }
  }
  return { notices, results };
}

export async function listSupplierAlertHistory({
  caseId,
  organizationId,
  portal = 'customer',
  vendorProfileId,
}: {
  caseId: string;
  organizationId: string;
  portal?: BreachEvidencePortal;
  vendorProfileId?: string;
}) {
  return invokeBreachNotificationCases<{ events: SupplierAlertHistoryEvent[] }>(
    {
      action: 'list_supplier_history',
      organizationId,
      caseId,
      portal,
      vendorProfileId,
    },
    'Supplier Alert history failed to load',
  );
}

export async function postSupplierBreachUpdate({
  caseId,
  message,
  organizationId,
  vendorProfileId,
}: {
  caseId: string;
  message: string;
  organizationId: string;
  vendorProfileId: string;
}) {
  return invokeBreachNotificationCases<{ postedAt: string }>(
    {
      action: 'post_supplier_update',
      organizationId,
      portal: 'vendor',
      vendorProfileId,
      caseId,
      updateMessage: message,
    },
    'Breach update could not be posted',
  );
}

export async function resolveSupplierBreachCase({
  caseId,
  organizationId,
  vendorProfileId,
}: {
  caseId: string;
  organizationId: string;
  vendorProfileId: string;
}) {
  return invokeBreachNotificationCases<{ resolvedAt: string }>(
    {
      action: 'resolve_supplier_case',
      organizationId,
      portal: 'vendor',
      vendorProfileId,
      caseId,
    },
    'Breach case resolution could not be saved',
  );
}

export async function listNewsAlertDispositions({
  articleIds = [],
  organizationId,
}: {
  articleIds?: string[];
  organizationId: string;
}) {
  return invokeBreachNotificationCases<{ dispositions: BreachArticleCustomerDisposition[] }>(
    {
      action: 'list_news_dispositions',
      organizationId,
      articleIds,
    },
    'News Alert dispositions failed to load',
  );
}

export async function setNewsAlertStatus({
  articleId,
  organizationId,
  status,
  resolutionNotes,
}: {
  articleId: string;
  organizationId: string;
  status: NewsAlertCustomerStatus;
  resolutionNotes?: string | null;
}) {
  return invokeBreachNotificationCases<{ disposition: BreachArticleCustomerDisposition }>(
    {
      action: 'set_news_disposition',
      organizationId,
      articleId,
      dispositionStatus: status,
      resolutionNotes: resolutionNotes ?? null,
    },
    'News Alert status could not be saved',
  );
}

// ENT-2733: mark many News Alerts (feed signals) in one or more backend
// requests (chunked at BREACH_BULK_DISPOSITION_LIMIT), aggregating results.
export async function setNewsAlertStatusBulk({
  articleIds,
  organizationId,
  status,
}: {
  articleIds: string[];
  organizationId: string;
  status: NewsAlertCustomerStatus;
}) {
  const dispositions: BreachArticleCustomerDisposition[] = [];
  const results: BreachBulkDispositionResult[] = [];
  const chunks = chunk(articleIds, BREACH_BULK_DISPOSITION_LIMIT);
  for (let index = 0; index < chunks.length; index++) {
    const batch = chunks[index];
    let systemic = false;
    try {
      const response = await invokeBreachNotificationCases<{
        dispositions: BreachArticleCustomerDisposition[];
        results: BreachBulkDispositionResult[];
      }>(
        {
          action: 'set_news_dispositions_bulk',
          organizationId,
          articleIds: batch,
          dispositionStatus: status,
        },
        'News Alert statuses could not be saved',
      );
      dispositions.push(...response.dispositions);
      results.push(...response.results);
      systemic = chunkResultsAreSystemic(response.results);
    } catch (error) {
      await captureBulkDispositionChunkFailure(error, {
        action: 'set_news_dispositions_bulk',
        organizationId,
        batchSize: batch.length,
      });
      foldFailedChunk(results, batch, error);
      systemic = true;
    }
    // Round 9: stop fanning out further chunks at a dependency that just failed
    // systemically; mark the unscheduled ids failed without more requests.
    if (systemic) {
      failUnscheduledChunks(results, chunks.slice(index + 1));
      break;
    }
  }
  return { dispositions, results };
}

export async function markNewsAlertViewed({
  articleId,
  organizationId,
}: {
  articleId: string;
  organizationId: string;
}) {
  return invokeBreachNotificationCases<{ disposition: BreachArticleCustomerDisposition }>(
    {
      action: 'mark_news_viewed',
      organizationId,
      articleId,
    },
    'News Alert view could not be recorded',
  );
}

export async function listNewsAlertHistory({
  articleId,
  organizationId,
}: {
  articleId: string;
  organizationId: string;
}) {
  return invokeBreachNotificationCases<{
    events: NewsAlertHistoryEvent[];
    actions: NewsAlertLinkedAction[];
  }>(
    {
      action: 'list_news_history',
      organizationId,
      articleId,
    },
    'News Alert history failed to load',
  );
}

export async function createNewsAlertAction({
  articleId,
  organizationId,
}: {
  articleId: string;
  organizationId: string;
}) {
  return invokeBreachNotificationCases<{
    action: NewsAlertLinkedAction;
    created: boolean;
  }>(
    {
      action: 'create_news_action',
      organizationId,
      articleId,
    },
    'News Alert Action could not be created',
  );
}

export async function listBreachReceivers({
  organizationId,
}: {
  organizationId: string;
}) {
  return invokeBreachNotificationCases<{ members: BreachReceiverMember[]; delivery: BreachDeliveryPlan }>(
    {
      action: 'list_receivers',
      organizationId,
    },
    'Breach notification receivers failed to load',
  );
}

export async function upsertBreachReceivers({
  assignments,
  organizationId,
}: {
  assignments: BreachReceiverAssignment[];
  organizationId: string;
}) {
  return invokeBreachNotificationCases<{ updated: number }>(
    {
      action: 'upsert_receivers',
      organizationId,
      receiverAssignments: assignments,
    },
    'Breach notification receivers could not be updated',
  );
}

export async function listBreachNotificationObligations({
  organizationId,
  portal = 'customer',
  vendorProfileId,
}: {
  organizationId: string;
  portal?: 'customer' | 'vendor';
  vendorProfileId?: string;
}) {
  return invokeBreachNotificationCases<{ obligations: BreachNotificationObligation[] }>(
    {
      action: 'list_obligations',
      organizationId,
      portal,
      vendorProfileId,
    },
    'Breach notification obligations failed to load',
  );
}

export async function upsertBreachNotificationObligation({
  draft,
  organizationId,
  portal = 'customer',
  vendorProfileId,
}: {
  draft: BreachNotificationObligationDraft;
  organizationId: string;
  portal?: 'customer' | 'vendor';
  vendorProfileId?: string;
}) {
  return invokeBreachNotificationCases<{ upserted: boolean }>(
    {
      action: 'upsert_obligation',
      organizationId,
      portal,
      vendorProfileId,
      obligationDraft: draft,
    },
    'Breach notification obligation could not be saved',
  );
}
